import ClassBreaksRenderer from "@arcgis/core/renderers/ClassBreaksRenderer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import { useContext, useEffect, useMemo } from "react";
import { MapContext } from "../../App/MapContextContainer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import { useTranslation } from "react-i18next";

const colors: string[] = [
    "white",
    "#9fa940",
    "#ecc068",
    "#f6842c",
    "#ea7687",
    "#f0050d"];

export const MAX_SCALE_TO_SHOW_GRID = 85000;

const useGridRenderer = (stepHighEdges: number[], gridTitle: string) => {
    const mapContext = useContext(MapContext);
    const { t, i18n } = useTranslation("common");

    const grid25kmGeojsonLayer = useMemo(() => new FeatureLayer({
        source: [],
        renderer: new SimpleRenderer({
            symbol: new SimpleFillSymbol({
                style: 'none',
                outline: {
                    style: "none"
                }
            })
        }),
        title: "25 km karttalehtijako (piilotettu)",
        labelsVisible: false,
        legendEnabled: false,
        objectIdField: "fakeoid",
        listMode: 'hide',
        spatialReference: { wkid: 4326 },
        outFields: ["*"],
        geometryType: "polygon",
        popupEnabled: false,
        fields: [{
            name: "LEHTITUNNU",
            type: "string"
        }]
    }), []);

    const visibleGrid25kmGeojsonLayer = useMemo(() => new FeatureLayer({
        source: [],
        title: gridTitle,
        labelsVisible: false,
        objectIdField: "fakeoid",
        opacity: 0.5,
        maxScale: MAX_SCALE_TO_SHOW_GRID,
        geometryType: "polygon",
        spatialReference: { wkid: 4326 },
        outFields: ["*"],
        popupEnabled: false,
        legendEnabled: true,
        fields: [{
            name: "clashCount",
            type: "double"
        }]
    }), [gridTitle]);

    const gridRenderer = useMemo(() => {
        const r = new ClassBreaksRenderer({
            field: "clashCount",
            defaultLabel: "Foo",
            legendOptions: {
                title: t("gridLegendOtsikko")
            }
        });

        let previousEdge = 0;

        stepHighEdges.forEach((highEdge, index) => {
            r.addClassBreakInfo({
                minValue: previousEdge,
                maxValue: (index === stepHighEdges.length - 1) ? 999 : highEdge, // Make last maxValue open-ended
                symbol: new SimpleFillSymbol({
                    color: colors[index],
                    style: 'solid',
                    outline: {
                        style: "none"
                    }
                })
            });

            previousEdge = highEdge + 1;
        });

        return r;
    }, [stepHighEdges, t, i18n.language]);

    useEffect(() => {
        if (gridRenderer) {
            visibleGrid25kmGeojsonLayer.renderer = gridRenderer;
        }
    }, [mapContext.gridOpacity, gridRenderer, visibleGrid25kmGeojsonLayer]);

    return {
        gridRenderer,
        visibleGrid25kmGeojsonLayer,
        grid25kmGeojsonLayer
    };
};

export default useGridRenderer;